import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Box, Button, Flex, Icon, Stack, Text, useColorModeValue } from "@chakra-ui/react";
import { MdReplay, MdWarningAmber } from "react-icons/md";
import { getDocumentRoleLabel } from "./certificationUtils";

const REASON_LABELS = {
    download_failed: "Téléchargement du document échoué",
    stripe_upload_failed: "Envoi vers Stripe échoué",
};

/**
 * Tests whether the certification has a Stripe transfer issue worth surfacing:
 * explicit transfer errors, or an accepted cert whose stripe_file_ids is empty.
 * @param {Object} certification - Certification object
 * @returns {boolean}
 */
function hasStripeIssue(certification) {
    if (!certification) return false;
    const errors = certification.stripe_transfer_error;
    if (Array.isArray(errors) && errors.length > 0) return true;
    if (certification.status !== "accepted") return false;
    const ids = certification.stripe_file_ids;
    if (!ids) return true;
    return typeof ids === "object" && Object.keys(ids).length === 0;
}

/**
 * Calls the retry Stripe transfer endpoint and notifies the parent on success.
 * @param {Object} args - { certificationId, setLoading, onCertRefreshed }
 * @returns {Promise<void>}
 */
async function runRetry(args) {
    const { certificationId, setLoading, onCertRefreshed } = args;
    setLoading(true);
    try {
        const response = await axios.post(`/admin/certifications/${certificationId}/retry_stripe_transfer`);
        toast.success("Transfert Stripe relancé avec succès.");
        if (response?.data && onCertRefreshed) onCertRefreshed(response.data);
    } catch (error) {
        toast.error(error?.response?.data?.message || "Échec du transfert Stripe.");
    } finally {
        setLoading(false);
    }
}

/**
 * Builds the human-readable label for a single transfer error entry.
 * @param {Object} err - { role, reason, message }
 * @returns {string}
 */
function describeError(err) {
    const roleCfg = getDocumentRoleLabel(err?.role);
    const rolePart = roleCfg ? `${roleCfg.label} — ` : "";
    return `${rolePart}${REASON_LABELS[err?.reason] || err?.reason || "Erreur"}`;
}

/**
 * Renders the list of structured transfer errors, or a generic notice when the
 * transfer simply produced no file ids.
 * @param {Object} props
 * @param {Array<Object>} props.errors - Structured error entries
 * @returns {React.ReactElement}
 */
function ErrorList({ errors }) {
    const msgColor = useColorModeValue("secondaryGray.700", "secondaryGray.300");
    if (!Array.isArray(errors) || errors.length === 0) {
        return <Text fontSize="sm" color={msgColor}>Aucun document n'a été transféré vers Stripe.</Text>;
    }
    return (
        <Stack spacing="6px">
            {errors.map((err, idx) => (
                <Box key={idx}>
                    <Text fontSize="sm" fontWeight="600" color={msgColor}>{describeError(err)}</Text>
                    {err?.message && <Text fontSize="xs" color={msgColor}>{err.message}</Text>}
                </Box>
            ))}
        </Stack>
    );
}

/**
 * Warning panel summarizing Stripe Files transfer problems for an accepted
 * certification and offering a one-click retry. Surfaces the API's
 * `stripe_transfer_error` so the admin can diagnose without opening Sentry.
 * Hidden when there is no transfer issue.
 * @param {Object} props
 * @param {Object} props.certification - The certification being viewed
 * @param {Function} props.onCertRefreshed - Callback receiving the refreshed cert
 * @returns {React.ReactElement|null}
 */
function StripeTransferPanel({ certification, onCertRefreshed }) {
    const [loading, setLoading] = useState(false);
    const bg = useColorModeValue("orange.50", "whiteAlpha.100");
    const border = useColorModeValue("orange.200", "whiteAlpha.300");
    if (!hasStripeIssue(certification)) return null;
    const handleRetry = () => runRetry({ certificationId: certification.id, setLoading, onCertRefreshed });
    return (
        <Box p="14px" bg={bg} borderRadius="12px" border="1px solid" borderColor={border}>
            <Flex align="center" gap="8px" mb="8px">
                <Icon as={MdWarningAmber} w="18px" h="18px" color="orange.400" />
                <Text fontSize="sm" fontWeight="700" color="orange.400">Transfert Stripe incomplet</Text>
            </Flex>
            <ErrorList errors={certification.stripe_transfer_error} />
            <Button mt="12px" size="sm" colorScheme="orange" variant="outline" isLoading={loading}
                loadingText="Relance..." leftIcon={<Icon as={MdReplay} />} onClick={handleRetry}>
                Relancer le transfert
            </Button>
        </Box>
    );
}

export default StripeTransferPanel;
